// Make elements resizable with handles
export function makeResizable(element, options = {}) {
  const {
    minWidth = 300,
    minHeight = 200,
    maxWidthPercent = 80,
    maxHeightPercent = 80,
    handles = ['n', 'e', 's', 'w', 'ne', 'se', 'sw', 'nw'],
    onResize = null,
    storageKey = null
  } = options;
  
  let isResizing = false;
  let currentHandle = null;
  let startX = 0;
  let startY = 0;
  let startWidth = 0;
  let startHeight = 0;
  let startLeft = 0;
  let startTop = 0;
  
  // Create resize handles
  const handleElements = {};
  handles.forEach(handle => {
    const handleEl = document.createElement('div');
    handleEl.className = `resize-handle resize-handle-${handle}`;
    handleEl.dataset.handle = handle;
    
    // Style the handles
    const baseStyle = {
      position: 'absolute',
      zIndex: '1000'
    };
    
    // Expanded hot zones for better touch interaction
    const handleStyles = {
      n: { top: '-8px', left: '20px', right: '20px', height: '16px', cursor: 'ns-resize' },
      s: { bottom: '-8px', left: '20px', right: '20px', height: '16px', cursor: 'ns-resize' },
      e: { top: '20px', bottom: '20px', right: '-8px', width: '16px', cursor: 'ew-resize' },
      w: { top: '20px', bottom: '20px', left: '-8px', width: '16px', cursor: 'ew-resize' },
      ne: { top: '-8px', right: '-8px', width: '20px', height: '20px', cursor: 'nesw-resize' },
      se: { bottom: '-8px', right: '-8px', width: '20px', height: '20px', cursor: 'nwse-resize' },
      sw: { bottom: '-8px', left: '-8px', width: '20px', height: '20px', cursor: 'nesw-resize' },
      nw: { top: '-8px', left: '-8px', width: '20px', height: '20px', cursor: 'nwse-resize' }
    };
    
    Object.assign(handleEl.style, baseStyle, handleStyles[handle]);
    handleElements[handle] = handleEl;
    element.appendChild(handleEl);
  });
  
  
  function getMaxDimensions() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    return {
      maxWidth: (viewportWidth * maxWidthPercent) / 100,
      maxHeight: (viewportHeight * maxHeightPercent) / 100
    };
  }
  
  function startResize(e) {
    const handle = e.target.dataset.handle;
    if (!handle) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    isResizing = true;
    currentHandle = handle;
    
    const touch = e.type.includes('touch');
    startX = touch ? e.touches[0].clientX : e.clientX;
    startY = touch ? e.touches[0].clientY : e.clientY;
    
    const rect = element.getBoundingClientRect();
    startWidth = rect.width;
    startHeight = rect.height;
    startLeft = element.offsetLeft;
    startTop = element.offsetTop;
    
    // Add overlay to prevent iframe/content interference
    const overlay = document.createElement('div');
    overlay.id = 'resize-overlay';
    overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; cursor: inherit;';
    document.body.appendChild(overlay);
  }
  
  function doResize(e) {
    if (!isResizing) return;
    
    e.preventDefault();
    
    const touch = e.type.includes('touch');
    const currentX = touch ? e.touches[0].clientX : e.clientX;
    const currentY = touch ? e.touches[0].clientY : e.clientY;
    
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    
    let newWidth = startWidth;
    let newHeight = startHeight;
    let newLeft = startLeft;
    let newTop = startTop;
    
    // Calculate new dimensions based on handle
    if (currentHandle.includes('e')) {
      newWidth = startWidth + deltaX;
    }
    if (currentHandle.includes('w')) {
      newWidth = startWidth - deltaX;
      newLeft = startLeft + deltaX;
    }
    if (currentHandle.includes('s')) {
      newHeight = startHeight + deltaY;
    }
    if (currentHandle.includes('n')) {
      newHeight = startHeight - deltaY;
      newTop = startTop + deltaY;
    }
    
    // Apply constraints
    const { maxWidth, maxHeight } = getMaxDimensions();
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    
    // Adjust position if resizing from top/left
    if (currentHandle.includes('w')) {
      newLeft = startLeft + (startWidth - newWidth);
    }
    if (currentHandle.includes('n')) {
      newTop = startTop + (startHeight - newHeight);
    }
    
    // Apply new dimensions
    element.style.width = newWidth + 'px';
    element.style.height = newHeight + 'px';
    
    if (currentHandle.includes('w') || currentHandle.includes('n')) {
      element.style.left = newLeft + 'px';
      element.style.top = newTop + 'px';
    }
    
    // Call resize callback
    if (onResize) {
      onResize(newWidth, newHeight);
    }
  }
  
  function endResize(e) {
    if (!isResizing) return;
    
    isResizing = false;
    currentHandle = null;
    
    // Remove overlay
    const overlay = document.getElementById('resize-overlay');
    if (overlay) {
      overlay.remove();
    }
    
    // Trigger callback
    if (onResize) {
      const rect = element.getBoundingClientRect();
      onResize(rect.width, rect.height);
    }
  }
  
  // Add event listeners to handles
  Object.values(handleElements).forEach(handle => {
    handle.addEventListener('mousedown', startResize);
    handle.addEventListener('touchstart', startResize, { passive: false });
  });
  
  // Document-wide listeners for move and end
  document.addEventListener('mousemove', doResize);
  document.addEventListener('mouseup', endResize);
  document.addEventListener('touchmove', doResize, { passive: false });
  document.addEventListener('touchend', endResize);
  
  
  // Return cleanup function
  return () => {
    Object.values(handleElements).forEach(handle => {
      handle.removeEventListener('mousedown', startResize);
      handle.removeEventListener('touchstart', startResize);
      handle.remove();
    });
    document.removeEventListener('mousemove', doResize);
    document.removeEventListener('mouseup', endResize);
    document.removeEventListener('touchmove', doResize);
    document.removeEventListener('touchend', endResize);
  };
}